import { useEventListener } from '@vueuse/core';
import { ref } from 'vue';
import { useToast } from '#imports';
import { featureFlagsVisible } from './state';

const MAGIC_SEQUENCE = ['f', 'l', 'a', 'g', 's', 'enter'] as const;

function normalizeKey(key: string) {
	return key.toLowerCase();
}

export function isEditableEventTarget(target: EventTarget | null) {
	if (typeof HTMLElement === 'undefined' || !(target instanceof HTMLElement)) {
		return false;
	}

	if (target.isContentEditable) {
		return true;
	}

	return target.closest('input, textarea, [contenteditable=""], [contenteditable="true"]') !== null;
}

export function useFeatureFlagKeyboardShortcuts() {
	const isCommandMode = ref(false);
	const sequenceIndex = ref(0);

	const toast = useToast();

	function reset() {
		isCommandMode.value = false;
		sequenceIndex.value = 0;
	}

	function handleKeydown(event: KeyboardEvent) {
		if (isEditableEventTarget(event.target)) {
			return;
		}

		const normalizedKey = normalizeKey(event.key);

		if (
			(event.ctrlKey || event.metaKey)
			&& event.altKey === false
			&& event.shiftKey === false
			&& normalizedKey === 'i'
		) {
			event.preventDefault();
			isCommandMode.value = true;
			sequenceIndex.value = 0;

			return;
		}

		if (isCommandMode.value === false) {
			return;
		}

		if (event.altKey || event.metaKey || event.ctrlKey) {
			reset();
			return;
		}

		if (normalizedKey !== MAGIC_SEQUENCE[sequenceIndex.value]) {
			reset();
			return;
		}

		event.preventDefault();

		if (sequenceIndex.value === MAGIC_SEQUENCE.length - 1) {
			featureFlagsVisible.value = true;

			toast.add({
				title: 'Feature Flags enabled',
				description: 'You can now open the feature flags from your user menu.',
				icon: 'lucide:flag',
				color: 'success',
			});

			reset();
			return;
		}

		sequenceIndex.value += 1;
	}

	useEventListener(window, 'keydown', handleKeydown);

	return {
		isCommandMode,
		sequenceIndex,
		handleKeydown,
		reset,
	};
}
